// app/src/features/submit/useSubmitState.js
import { useRef, useState } from "react";

/**
 * The submit bucket's React state and refs: the global `loading` guard, the in-flight stream's
 * abort handle, the error streak, the preprocess confirmation panel with its per-submission flags,
 * the "awaiting" phase and the in-flight trace. Called first (where `AppInner` declared `loading`),
 * before every reader: `useSubmitWiring` and `useSubmitEffects` take these as inputs, the other
 * hooks of `AppInner` destructure them, and the frame context spreads them. The composer's `input`
 * stays `AppInner`'s (the chat bucket's typing-focus effect reads its setter unlisted, which the
 * exhaustive-deps rule accepts of a sibling useState setter, not of a hook's output).
 */
export function useSubmitState() {
  const [loading, setLoading] = useState(false);
  const forecastAbortRef = useRef(null);
  // Consecutive forecast-error streak — drives the Sayer retry nudge (try again →
  // submit a bug). Owned here so it survives across submissions; reset on success.
  const forecastErrorStreakRef = useRef(0);
  const [pendingPreprocess, setPendingPreprocess] = useState(null); // {original, question, engine, msgs}
  // True once the user has confirmed (or implicitly accepted via dismiss / edit-
  // resubmit) the preprocess panel for the CURRENT submission. Reset to false
  // on every submit() start. Read by resetForecast to decide whether a CLEAR
  // mid-flight should also discard the pending forecast (false → discard +
  // cancel server; true → keep the log entry, just abort locally).
  const preprocessConfirmedRef = useRef(false);
  // AbortController for the whole submit pipeline (preprocess fetch + the
  // delay until doForecast actually owns its own forecastAbortRef). Lets
  // resetForecast() short-circuit a CLEAR that lands while preprocess is
  // still in flight — without this, preprocess would resolve into a stale
  // setPendingPreprocess + doForecast call after the user already cleared.
  const submitAbortRef = useRef(null);
  const [forecastingPhase, setForecastingPhase] = useState(null); // null | "awaiting" — true while forecast is generating
  // Active in-flight forecast trace_id. Populated when the SSE proxy's
  // first response header arrives (via onTraceAllocated). Cleared on any
  // terminal event so the Cancel button hides exactly when there's
  // nothing to cancel. Carries the chatboard forecast UUID alongside the
  // trace_id (different keys).
  const [activeTrace, setActiveTrace] = useState(null); // null | {traceId, forecastId}
  return {
    loading,
    setLoading,
    forecastAbortRef,
    forecastErrorStreakRef,
    pendingPreprocess,
    setPendingPreprocess,
    preprocessConfirmedRef,
    submitAbortRef,
    forecastingPhase,
    setForecastingPhase,
    activeTrace,
    setActiveTrace,
  };
}
